/** @jsxRuntime classic */ // <-- remove this when TS updated to 4.1
/** @jsx jsx */ // <-- remove this when TS updated to 4.1
import { jsx } from '@emotion/core';
import { FC, Fragment, useContext, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { PaymentMethodPayload } from 'braintree-web-drop-in';
import { Button } from '../..';
import useBraintree from '../../../hooks/useBraintree';
import { useMutation } from 'react-query';
import { generateSignedRequestConfig, isEmpty } from '../../../utils';
import { appStore } from '../../../state';
import { Copy, ROUTES } from '../../../types';
import { baseURL, postBtPaymentData, patchPaymentCardData } from '../../../api';
import { useGetClientToken } from '../../../hooks';

const BraintreeDropin: FC<Copy> = ({ fallbackCopy, localeCopy }) => {
  const history = useHistory();
  const { dispatch, state } = useContext(appStore);
  const [clientToken, setClientToken] = useState<string>('');
  const { form, user, params } = state;
  const { apiKey, apiSecret, countryCode, id, payment } = user;
  const {locale} = params;
  const {
    isLoading: clientTokenIsLoading,
    refetch: fetchClientToken,
  } = useGetClientToken({
    queryConfig: {
      enabled: false,
      onSuccess: (data: any) => {
        setClientToken(data); // bug in library where the data was not re-rendering the component of manually triggered, so using a react hook to rerender the component.
      },
    },
    user,
  });

  useEffect(() => {
    fetchClientToken();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const {
    btInstance,
    error: useBraintreeError,
    isBraintreeReady,
  } = useBraintree({
    containerId: '#braintreePanel',
    token: clientToken,
    locale: locale,
  });

  const {
    isLoading: patchPaymentCardIsLoading,
    mutateAsync: patchPaymentCard,
  } = useMutation(patchPaymentCardData, {
    onError: () => {},
    onSuccess: async (data: any) => {},
  });

  const { isLoading: postBtDataIsLoading, mutate: postBtData } = useMutation(
    postBtPaymentData,
    {
      onError: (error) => {
        console.error('Error posting card payment to API: ', error);
        dispatch({
          type: 'ADD_MESSAGE',
          payload: {
            dismissable: true,
            id: new Date().getTime(),
            text:
              localeCopy?.errors?.PAYMENT_ERROR ||
              fallbackCopy.errors.PAYMENT_ERROR,
            timeout: 7,
            type: 'error',
          },
        });
      },
      onSuccess: async (data: any) => {
        /**
         * save subscription data
         */
        await dispatch({
          type: 'UPDATE_USER',
          payload: {
            subscription: {
              ...data,
            },
          },
        });

        await patchPaymentCard({
          config: generateSignedRequestConfig({
            apiKey,
            apiSecret,
            countryCode,
            data: {
              paymentToken: payment.details.nonce || null,
              cardBrand: payment.details.cardType || null,
              cardNo: payment.details.lastFour || null,
            },
            id,
            method: 'PATCH',
            url: `${baseURL}/api/billing/user/${id}/paymentCard`,
          }),
        });

        history.replace(ROUTES.PAYMENT_SUCCESS);
      },
    }
  );

  const handleClick = () => {
    !isEmpty(btInstance) &&
      btInstance!.requestPaymentMethod(
        (err, { details, deviceData, nonce, type }: PaymentMethodPayload) => {
          if (err) {
            console.error('Error in BT requestPaymentMethod callback: ', err);
            // Handle errors in requesting payment method
            dispatch({
              type: 'ADD_MESSAGE',
              payload: {
                dismissable: true,
                id: new Date().getTime(),
                text:
                  localeCopy?.errors?.PAYMENT_ERROR ||
                  fallbackCopy.errors.PAYMENT_ERROR,
                timeout: 7,
                type: 'error',
              },
            });
            return;
          }

          dispatch({
            type: 'UPDATE_USER',
            payload: {
              payment: {
                type,
                details,
              },
            },
          });

          try {
            postBtData({
              config: generateSignedRequestConfig({
                apiKey,
                apiSecret,
                countryCode,
                data: {
                  planId: form.selectedPlan?.productId,
                  customerId: user.id,
                  paymentMethodNonce: nonce,
                  deviceData,
                },
                id,
                method: 'POST',
                url: `${baseURL}/api/billing/braintree/subscription`,
              }),
            });
          } catch (error) {
            console.error(error);
          }
        }
      );
  };

  useEffect(() => {
    if (useBraintreeError) {
      console.error('Error in `useBraintree` hook: ', useBraintreeError);
      dispatch({
        type: 'ADD_MESSAGE',
        payload: {
          dismissable: true,
          id: new Date().getTime(),
          text:
            localeCopy?.errors?.GENERIC_ERROR ||
            fallbackCopy.errors.GENERIC_ERROR,
          timeout: 7,
          type: 'error',
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [useBraintreeError]);

  return (
    <Fragment>
      <div id='braintreePanel' css={{ width: '100%' }} />
      {!isBraintreeReady ? (
        <p>Loading...</p>
      ) : (
        <Button
          css={{ marginTop: 10 }}
          isBusy={
            clientTokenIsLoading ||
            patchPaymentCardIsLoading ||
            postBtDataIsLoading
          }
          isBusyText={
            localeCopy?.components?.button?.loading ||
            fallbackCopy?.components.button.loading
          }
          onClick={handleClick}
          onKeyUp={(e: any) => {
            if (e.key === 'Enter') {
              handleClick();
            }
          }}>
          {localeCopy?.paymentMethods?.braintree.button ||
            fallbackCopy.paymentMethods.braintree.button}
        </Button>
      )}
    </Fragment>
  );
};

export default BraintreeDropin;
